/**
 * Shoreline — progressive enhancement only.
 *
 * Two behaviours, both optional: reveal-on-scroll and the bestsellers rail
 * arrows. The design gated section opacity on JS state; here the rail is a
 * native scroll-snap container and reveal is a CSS class, so with JS off or
 * reduced motion on, every section renders at rest and the rail still scrolls.
 */
(function () {
  'use strict';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- reveal */

  // .sl-rev fades a whole section in; .sl-anim only receives .is-in, and the
  // CSS for each component (note tiers, gauge, curves, timeline) decides what
  // that entrance looks like.
  var REVEAL = '.sl-rev, .sl-anim';

  /**
   * Counts a [data-sl-count] figure up from zero. The server renders the
   * final value, so with reduced motion or no JS the number is simply there.
   */
  /**
   * For data-sl-count="auto": splits a figure such as "8 hrs" or "4.98★" into
   * the text before the number, the number, and the text after it, so the
   * count keeps its prefix, suffix and decimal places.
   */
  function countParts(text) {
    var m = String(text).match(/^(\D*?)(\d+(?:\.\d+)?)(.*)$/);
    if (!m) return null;
    var dec = m[2].indexOf('.') > -1 ? m[2].split('.')[1].length : 0;
    return { pre: m[1], target: parseFloat(m[2]), dec: dec, suf: m[3] };
  }

  function countUp(el) {
    if (el.dataset.slCounted) return;
    el.dataset.slCounted = 'true';

    var final = el.dataset.slFinal || el.textContent;
    var parts = el.dataset.slCount === 'auto' ? countParts(final) : null;
    var target = parts ? parts.target : parseFloat(el.dataset.slCount);
    if (reduced || !isFinite(target) || typeof requestAnimationFrame === 'undefined') {
      el.textContent = final;
      return;
    }

    var t0 = null;
    var dur = parts ? 1600 : 2100;
    function step(now) {
      if (t0 === null) t0 = now;
      var k = Math.min(1, (now - t0) / dur);
      var eased = 1 - Math.pow(1 - k, 3);
      if (k >= 1) el.textContent = final;
      else if (parts) el.textContent = parts.pre + (target * eased).toFixed(parts.dec) + parts.suf;
      else el.textContent = (target * eased).toFixed(1);
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function arrive(el) {
    el.classList.add('is-in');
    Array.prototype.forEach.call(el.querySelectorAll('[data-sl-count]'), countUp);
  }

  function revealAll(nodes) {
    for (var i = 0; i < nodes.length; i++) arrive(nodes[i]);
  }

  function initReveal() {
    var nodes = document.querySelectorAll(REVEAL);
    if (!nodes.length) return;

    if (reduced || typeof IntersectionObserver === 'undefined') {
      revealAll(nodes);
      return;
    }

    // Counters start from zero only when they are going to animate.
    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-count]'), function (c) {
      if (c.dataset.slCounted) return;
      if (!c.dataset.slFinal) c.dataset.slFinal = c.textContent;
      var p = c.dataset.slCount === 'auto' ? countParts(c.dataset.slFinal) : null;
      if (c.dataset.slCount === 'auto' && !p) return;
      c.textContent = p ? p.pre + (0).toFixed(p.dec) + p.suf : '0';
    });

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          arrive(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: '0px 0px -8% 0px' }
    );

    for (var i = 0; i < nodes.length; i++) io.observe(nodes[i]);

    // A backgrounded or prerendered document never fires the observer; make
    // sure nothing stays invisible once it becomes visible.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) revealAll(document.querySelectorAll(REVEAL));
    });
  }

  /* ------------------------------------------------------------ family tilt */

  /**
   * Fans the family tiles: outer tiles yaw outward, centre tiles lift forward.
   * Values come from the tile's index so the section works with any count.
   */
  function initFamilyTilt() {
    var grids = document.querySelectorAll('.sl-fam--tilt .sl-fam__grid');

    Array.prototype.forEach.call(grids, function (grid) {
      var cards = grid.querySelectorAll('.sl-fam__card');
      var n = cards.length;
      if (!n) return;

      Array.prototype.forEach.call(cards, function (card, i) {
        var t = n === 1 ? 0 : (i / (n - 1)) * 2 - 1;
        card.style.setProperty('--fam-yaw', (-t * 15).toFixed(2) + 'deg');
        card.style.setProperty('--fam-lift', ((1 - Math.abs(t)) * 22).toFixed(1) + 'px');
      });
    });
  }

  /* -------------------------------------------------------------- carousel */

  function initCarousels() {
    var rails = document.querySelectorAll('[data-sl-carousel]');

    Array.prototype.forEach.call(rails, function (rail) {
      var viewport = rail.querySelector('.sl-best__viewport');
      var prev = rail.querySelector('[data-sl-prev]');
      var next = rail.querySelector('[data-sl-next]');
      if (!viewport || !prev || !next || rail.dataset.slRailBound) return;
      rail.dataset.slRailBound = 'true';
      var progress = rail.querySelector('[data-sl-progress]');

      function step() {
        var slide = viewport.querySelector('.sl-best__slide');
        if (!slide) return viewport.clientWidth;
        var rect = slide.getBoundingClientRect();
        var style = window.getComputedStyle(slide);
        var margin = parseFloat(style.marginLeft) + parseFloat(style.marginRight);
        var per = Math.max(1, Math.floor(viewport.clientWidth / (rect.width + margin)));
        return (rect.width + margin) * per;
      }

      function sync() {
        // 2px tolerance absorbs sub-pixel scroll positions at the extremes.
        var max = viewport.scrollWidth - viewport.clientWidth;
        prev.disabled = viewport.scrollLeft <= 2;
        next.disabled = viewport.scrollLeft >= max - 2;

        if (progress) {
          // The thumb is as wide as the visible share of the rail and travels
          // across the rest of the bar as it scrolls.
          var share = viewport.scrollWidth ? Math.min(1, viewport.clientWidth / viewport.scrollWidth) : 1;
          var ratio = max > 0 ? viewport.scrollLeft / max : 0;
          progress.style.setProperty('--best-thumb', (share * 100).toFixed(2) + '%');
          progress.style.setProperty('--best-pos', (((1 - share) / share) * ratio * 100).toFixed(2) + '%');
        }
      }

      function scrollBy(dir) {
        viewport.scrollBy({
          left: dir * step(),
          behavior: reduced ? 'auto' : 'smooth'
        });
      }

      /* Autoplay: advances every N seconds, wraps to the start, and stops for
         good once the shopper takes over. It also waits while the rail is off
         screen, hovered, focused or the tab is hidden.

         Touch screens never autoplay. There is no hover there to pause it, so
         the rail would slide out from under a thumb already reaching for a
         bottle — the tap then lands on the next card, or on nothing. */
      var canHover = !window.matchMedia || window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      var seconds = parseFloat(rail.dataset.slAutoplay || '0');
      var takenOver = false;
      var hovering = false;
      var onScreen = false;
      if (seconds > 0 && !reduced && canHover) {
        var timer = window.setInterval(function () {
          if (takenOver || hovering || !onScreen || document.hidden) return;
          if (rail.contains(document.activeElement)) return;
          var max = viewport.scrollWidth - viewport.clientWidth;
          if (max <= 2) return;
          if (viewport.scrollLeft >= max - 2) viewport.scrollTo({ left: 0, behavior: 'smooth' });
          else scrollBy(1);
        }, seconds * 1000);

        var stop = function () {
          takenOver = true;
          window.clearInterval(timer);
          // A smooth scroll still in flight swallows the tap that stopped it —
          // that is why tapping a moving card opened nothing. Freeze the rail
          // where it stands so this first tap reaches the card's link.
          var x = viewport.scrollLeft;
          viewport.style.scrollBehavior = 'auto';
          viewport.scrollLeft = x;
          viewport.style.scrollBehavior = '';
        };
        rail.addEventListener('pointerenter', function () { hovering = true; });
        rail.addEventListener('pointerleave', function () { hovering = false; });
        // pointerdown covers mouse, touch and pen: the rail stops the moment a
        // shopper reaches for a card, so it can never move out from under them.
        rail.addEventListener('pointerdown', stop, { passive: true });
        viewport.addEventListener('touchstart', stop, { passive: true });
        viewport.addEventListener('wheel', function (e) { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) stop(); }, { passive: true });

        if (typeof IntersectionObserver !== 'undefined') {
          new IntersectionObserver(function (entries) {
            onScreen = entries[0].isIntersecting;
          }, { threshold: 0.35 }).observe(viewport);
        } else {
          onScreen = true;
        }

        prev.addEventListener('click', stop);
        next.addEventListener('click', stop);
      }

      /* Card tilt towards the pointer (showcase only, mouse only). */
      if (rail.classList.contains('sl-best--showcase') && !reduced &&
          window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        viewport.addEventListener('pointermove', function (e) {
          var card = e.target.closest && e.target.closest('.sl-card');
          if (!card) return;
          var r = card.getBoundingClientRect();
          var x = (e.clientX - r.left) / r.width - 0.5;
          var y = (e.clientY - r.top) / r.height - 0.5;
          card.style.setProperty('--ry', (x * 10).toFixed(2) + 'deg');
          card.style.setProperty('--rx', (-y * 8).toFixed(2) + 'deg');
        });
        viewport.addEventListener('pointerout', function (e) {
          var card = e.target.closest && e.target.closest('.sl-card');
          if (card && !card.contains(e.relatedTarget)) {
            card.style.removeProperty('--rx');
            card.style.removeProperty('--ry');
          }
        });
      }

      prev.addEventListener('click', function () { scrollBy(-1); });
      next.addEventListener('click', function () { scrollBy(1); });
      viewport.addEventListener('scroll', sync, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(sync).observe(viewport);
      } else {
        window.addEventListener('resize', sync);
      }

      sync();
    });
  }

  /* --------------------------------------------------------------- filters */

  /**
   * Submits the collection filter form as soon as a control changes, so the
   * page behaves like a filtered view rather than a form to fill in. The
   * form is a plain GET, so without this it still works via the Apply
   * button shown in the <noscript> block.
   */
  function initFilters() {
    var form = document.getElementById('ShorelineFilters');
    if (!form || form.dataset.slBound) return;
    form.dataset.slBound = 'true';

    form.addEventListener('change', function () { form.submit(); });

    // The sort <select> lives outside the form and is bound by form=; Safari
    // does not support that attribute, so submit it explicitly.
    var sort = document.getElementById('ShorelineSort');
    if (sort && !form.contains(sort)) {
      sort.addEventListener('change', function () {
        var hidden = form.querySelector('input[name="sort_by"]');
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'sort_by';
          form.appendChild(hidden);
        }
        hidden.value = sort.value;
        form.submit();
      });
    }
  }

  /* ------------------------------------------------------------- product */

  /** Quantity stepper. The input itself stays usable if this never runs. */
  function initQty() {
    var input = document.getElementById('ShorelineQty');
    if (!input) return;

    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-qty]'), function (btn) {
      if (btn.dataset.slBound) return;
      btn.dataset.slBound = 'true';

      btn.addEventListener('click', function () {
        var min = parseInt(input.min, 10) || 1;
        var next = (parseInt(input.value, 10) || min) + parseInt(btn.dataset.slQty, 10);
        input.value = Math.max(min, next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /**
   * Product gallery: a scroll-snapped row of slides. Swiping (or the
   * thumbnails, which scroll the row) moves the dots and the current thumb.
   */
  function initGallery() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-gallery]'), function (g) {
      if (g.dataset.slBound) return;
      g.dataset.slBound = 'true';

      var track = g.querySelector('[data-sl-track]');
      if (!track) return;
      var dots = g.querySelectorAll('.sl-pdp__dot');
      var thumbs = g.querySelectorAll('[data-sl-thumb]');
      var cur = 0;

      function mark(i) {
        if (i === cur) return;
        cur = i;
        Array.prototype.forEach.call(dots, function (d, k) { d.classList.toggle('is-on', k === i); });
        Array.prototype.forEach.call(thumbs, function (t, k) {
          if (k === i) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
      }

      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          mark(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
        });
      }, { passive: true });

      Array.prototype.forEach.call(thumbs, function (t) {
        t.addEventListener('click', function () {
          var i = parseInt(t.getAttribute('data-sl-thumb'), 10) || 0;
          track.scrollTo({ left: i * track.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
          mark(i);
        });
      });
    });
  }

  /* -------------------------------------------------------- video stories */

  /**
   * Round video stories under the product name. A ring opens the full-screen
   * viewer: bars along the top fill as each clip plays, clips run on into the
   * next, the left third of the frame goes back and the rest goes forward.
   * Sound starts on, since the shopper tapped to watch; the browser falls
   * back to muted if it refuses.
   */
  function initStories() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-stories]'), function (root) {
      if (root.dataset.slBound) return;
      root.dataset.slBound = 'true';

      var items = root.querySelectorAll('[data-sl-story-item]');
      var dlg = root.querySelector('[data-sl-story]');
      // Without <dialog> support the rings stay plain links to the clips.
      if (!items.length || !dlg || typeof dlg.showModal !== 'function') return;

      var video = dlg.querySelector('video');
      var barsHost = dlg.querySelector('[data-sl-story-bars]');
      var muteBtn = dlg.querySelector('[data-sl-story-mute]');
      var bars = [];
      var idx = 0;
      var raf = 0;
      var opener = null;

      Array.prototype.forEach.call(items, function () {
        var bar = document.createElement('span');
        bar.className = 'sl-story__bar';
        bar.appendChild(document.createElement('i'));
        barsHost.appendChild(bar);
        bars.push(bar);
      });

      function tick() {
        var bar = bars[idx];
        if (bar && video.duration) {
          bar.firstChild.style.width = Math.min(100, (video.currentTime / video.duration) * 100) + '%';
        }
        raf = requestAnimationFrame(tick);
      }

      function play() {
        var p = video.play();
        if (p && p.catch) {
          p.catch(function () {
            // Refused or interrupted: retry muted, unless it was closed.
            if (!dlg.open) return;
            video.muted = true;
            muteBtn.setAttribute('aria-pressed', 'true');
            video.play().catch(function () {});
          });
        }
      }

      // Stops the clip and drops its source so nothing keeps downloading.
      // Runs from our own close buttons and again from the dialog's close
      // event (Escape), so it must be safe to run twice.
      function cleanup() {
        cancelAnimationFrame(raf);
        if (!video.getAttribute('src')) return;
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
      function shut() {
        cleanup();
        if (dlg.open) dlg.close();
      }

      function show(i) {
        if (i < 0) i = 0;
        if (i >= items.length) { shut(); return; }
        idx = i;
        bars.forEach(function (b, k) {
          b.classList.toggle('is-done', k < i);
          b.firstChild.style.width = k < i ? '100%' : '0';
        });
        items[i].classList.add('is-seen');
        video.poster = items[i].getAttribute('data-poster') || '';
        video.src = items[i].getAttribute('href');
        play();
      }

      Array.prototype.forEach.call(items, function (item, i) {
        item.addEventListener('click', function (e) {
          e.preventDefault();
          opener = item;
          dlg.showModal();
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(tick);
          show(i);
        });
      });

      video.addEventListener('ended', function () { show(idx + 1); });
      dlg.querySelector('[data-sl-story-prev]').addEventListener('click', function () {
        // Early in a clip, back means the previous one; later, its start.
        if (video.currentTime > 2 || idx === 0) { video.currentTime = 0; play(); }
        else show(idx - 1);
      });
      dlg.querySelector('[data-sl-story-next]').addEventListener('click', function () { show(idx + 1); });
      dlg.querySelector('[data-sl-story-close]').addEventListener('click', shut);
      muteBtn.addEventListener('click', function () {
        video.muted = !video.muted;
        muteBtn.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
        muteBtn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
      });
      // A click on the backdrop (outside the frame) closes.
      dlg.addEventListener('click', function (e) { if (e.target === dlg) shut(); });
      dlg.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') show(idx + 1);
        if (e.key === 'ArrowLeft') show(idx - 1);
      });
      dlg.addEventListener('close', function () {
        cleanup();
        if (opener) opener.focus();
      });

      var buy = dlg.querySelector('[data-sl-story-buy]');
      if (buy) {
        buy.addEventListener('click', function () {
          var real = document.querySelector('[data-sl-atc]');
          shut();
          if (real) real.click();
        });
      }
    });
  }

  /* -------------------------------------------------------- video reviews */

  /**
   * Video reviews row. Clips get their source only as the row nears the
   * screen. While the row is in view, the card nearest its middle plays muted
   * as a preview (a hovered card takes over on desktop); everything else
   * stays on its poster. Tapping a card opens the full-screen player with
   * sound and controls, which steps through the clips and runs on to the
   * next when one ends.
   */
  function initVideoReviews() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-vr]'), function (root) {
      if (root.dataset.slBound) return;
      root.dataset.slBound = 'true';

      var row = root.querySelector('[data-sl-vr-row]');
      var cards = Array.prototype.slice.call(root.querySelectorAll('[data-sl-vr-card]'));
      if (!row || !cards.length) return;
      var dlg = root.querySelector('[data-sl-vr-dlg]');
      var full = dlg && dlg.querySelector('video');
      var live = null;
      var visible = false;
      var idx = 0;

      function clip(card) {
        var v = card.querySelector('.sl-vr__vid');
        if (!v.getAttribute('src') && card.dataset.src) v.src = card.dataset.src;
        return v;
      }
      function play(v) {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      }
      function setLive(card) {
        if (card === live) {
          if (card && visible && !reduced) play(clip(card));
          return;
        }
        if (live) {
          live.querySelector('.sl-vr__vid').pause();
          live.classList.remove('is-live');
        }
        live = card;
        if (!card) return;
        card.classList.add('is-live');
        if (!reduced && visible && !(dlg && dlg.open)) {
          var v = clip(card);
          v.muted = true;
          play(v);
        }
      }
      function pick() {
        if (!visible || (dlg && dlg.open)) return;
        var rr = row.getBoundingClientRect();
        var mid = rr.left + rr.width / 2;
        var best = null, bestD = Infinity;
        cards.forEach(function (c) {
          var r = c.getBoundingClientRect();
          var d = Math.abs(r.left + r.width / 2 - mid);
          if (d < bestD) { bestD = d; best = c; }
        });
        setLive(best);
      }

      row.classList.add('is-ready');
      var ticking = false;
      row.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { ticking = false; pick(); });
      }, { passive: true });

      if (typeof IntersectionObserver === 'undefined') {
        visible = true;
        pick();
      } else {
        // Posters only until the row is close; then sources, then playback.
        new IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
          if (visible) pick();
          else if (live) live.querySelector('.sl-vr__vid').pause();
        }, { threshold: 0.35 }).observe(row);
      }

      var hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
      cards.forEach(function (c) {
        c.addEventListener('mouseenter', function () { if (hoverable.matches) setLive(c); });
      });

      function step(dir) {
        var w = cards[0].getBoundingClientRect().width + 14;
        row.scrollBy({ left: dir * w, behavior: reduced ? 'auto' : 'smooth' });
      }
      var prev = root.querySelector('[data-sl-vr-prev]');
      var next = root.querySelector('[data-sl-vr-next]');
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });

      /* ---- the player ---- */
      var canDialog = dlg && typeof dlg.showModal === 'function';
      var bar = dlg && dlg.querySelector('[data-sl-vr-bar]');
      var dprev = dlg && dlg.querySelector('[data-sl-vr-dprev]');
      var dnext = dlg && dlg.querySelector('[data-sl-vr-dnext]');

      function openAt(i) {
        idx = Math.max(0, Math.min(cards.length - 1, i));
        var c = cards[idx];
        if (live) live.querySelector('.sl-vr__vid').pause();
        full.poster = c.querySelector('.sl-vr__vid').getAttribute('poster') || '';
        full.src = c.dataset.src;
        full.muted = false;
        if (bar) {
          var has = !!c.dataset.product;
          bar.hidden = !has;
          if (has) {
            bar.querySelector('[data-sl-vr-who]').textContent = c.dataset.name ? c.dataset.name + ' on ' : 'Review of ';
            bar.querySelector('[data-sl-vr-pname]').textContent = c.dataset.product;
            var shop = bar.querySelector('[data-sl-vr-shop]');
            shop.href = c.dataset.url;
            shop.setAttribute('aria-label', 'Shop ' + c.dataset.product + (c.dataset.price ? ', ' + c.dataset.price : ''));
          }
        }
        if (dprev) dprev.disabled = idx === 0;
        if (dnext) dnext.disabled = idx === cards.length - 1;
        if (!dlg.open) dlg.showModal();
        var p = full.play();
        if (p && p.catch) {
          // Sound refused (rare after a tap): play muted; the controls can unmute.
          p.catch(function () { if (!dlg.open) return; full.muted = true; play(full); });
        }
      }
      function shut() {
        full.pause();
        full.removeAttribute('src');
        full.load();
        if (dlg.open) dlg.close();
      }

      cards.forEach(function (c, i) {
        c.querySelector('[data-sl-vr-open]').addEventListener('click', function () {
          if (canDialog) openAt(i);
          else window.open(c.dataset.src, '_blank', 'noopener');
        });
      });
      if (!canDialog) return;

      full.addEventListener('ended', function () { if (idx < cards.length - 1) openAt(idx + 1); });
      dprev.addEventListener('click', function () { openAt(idx - 1); });
      dnext.addEventListener('click', function () { openAt(idx + 1); });
      dlg.querySelector('[data-sl-vr-close]').addEventListener('click', shut);
      dlg.addEventListener('click', function (e) { if (e.target === dlg) shut(); });
      dlg.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') openAt(idx + 1);
        if (e.key === 'ArrowLeft') openAt(idx - 1);
      });
      dlg.addEventListener('close', function () {
        full.pause();
        if (full.getAttribute('src')) { full.removeAttribute('src'); full.load(); }
        var btn = cards[idx] && cards[idx].querySelector('[data-sl-vr-open]');
        if (btn) btn.focus();
        pick();
      });
    });
  }

  /* --------------------------------------------------------- review quote */

  /**
   * One review at a time under the price. Turns on by itself while on screen,
   * stops while the shopper hovers, focuses or touches it, and never moves
   * with reduced motion (the arrows still work).
   */
  function initQuotes() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-q]'), function (root) {
      if (root.dataset.slBound) return;
      root.dataset.slBound = 'true';

      var items = root.querySelectorAll('[data-sl-q-item]');
      if (items.length < 2) return;
      var count = root.querySelector('[data-sl-q-count]');
      var idx = 0;
      var held = false;
      var visible = false;
      var every = (parseInt(root.dataset.interval, 10) || 6) * 1000;
      var timer = null;

      function go(i) {
        idx = (i + items.length) % items.length;
        Array.prototype.forEach.call(items, function (it, k) { it.classList.toggle('is-on', k === idx); });
        if (count) count.textContent = (idx + 1) + ' / ' + items.length;
      }
      function arm() {
        clearInterval(timer);
        if (reduced || held || !visible) return;
        timer = setInterval(function () { go(idx + 1); }, every);
      }

      root.querySelector('[data-sl-q-prev]').addEventListener('click', function () { go(idx - 1); arm(); });
      root.querySelector('[data-sl-q-next]').addEventListener('click', function () { go(idx + 1); arm(); });
      ['mouseenter', 'focusin', 'touchstart'].forEach(function (ev) {
        root.addEventListener(ev, function () { held = true; arm(); }, { passive: true });
      });
      ['mouseleave', 'focusout'].forEach(function (ev) {
        root.addEventListener(ev, function () { held = false; arm(); });
      });

      if (typeof IntersectionObserver === 'undefined') {
        visible = true;
        arm();
      } else {
        new IntersectionObserver(function (entries) {
          visible = entries[0].isIntersecting;
          arm();
        }).observe(root);
      }
    });
  }

  /* ------------------------------------------------------------ coverflow */

  var STARS = '★★★★★';

  /** Reads the Judge.me reviews already rendered on the page into card data. */
  function ugcFromJudgeme(root) {
    var max = parseInt(root.dataset.max, 10) || 8;
    var fallback = root.dataset.fallbackImg || '';
    var cards = [];

    Array.prototype.forEach.call(document.querySelectorAll('#judgeme_product_reviews .jdgm-rev'), function (rev) {
      var body = rev.querySelector('.jdgm-rev__body');
      var text = body ? body.textContent.replace(/\s+/g, ' ').trim() : '';
      if (!text) return;

      var author = rev.querySelector('.jdgm-rev__author');
      var link = rev.querySelector('.jdgm-rev__pic-link');
      var pic = rev.querySelector('.jdgm-rev__pic-img');
      var img = (link && link.getAttribute('href')) || (pic && (pic.getAttribute('data-src') || pic.getAttribute('src'))) || '';
      var rating = rev.querySelector('.jdgm-rev__rating');

      cards.push({
        text: text,
        author: author ? author.textContent.trim() : '',
        img: img || fallback,
        photo: !!img,
        score: Math.max(1, Math.min(5, parseInt(rating && rating.getAttribute('data-score'), 10) || 5)),
        verified: rev.getAttribute('data-verified-buyer') === 'true'
      });
    });

    // Buyer photos lead; the product shot stands in for text-only reviews.
    cards.sort(function (a, b) { return (b.photo ? 1 : 0) - (a.photo ? 1 : 0); });
    return cards.slice(0, max);
  }

  /** Builds a card with DOM APIs, so review text is never parsed as HTML. */
  function buildCard(c) {
    var fig = document.createElement('figure');
    fig.className = 'sl-ugc__card';

    var media = document.createElement('div');
    media.className = 'sl-ugc__media';
    if (c.img) {
      var im = document.createElement('img');
      im.src = c.img;
      im.alt = c.photo ? 'Photo from ' + (c.author || 'a buyer') : '';
      im.loading = 'lazy';
      media.appendChild(im);
    }

    var body = document.createElement('div');
    body.className = 'sl-ugc__body';
    var stars = document.createElement('span');
    stars.className = 'sl-ugc__stars';
    stars.textContent = STARS.slice(0, c.score);
    stars.setAttribute('aria-label', c.score + ' out of 5 stars');
    var quote = document.createElement('blockquote');
    quote.className = 'sl-ugc__quote';
    quote.textContent = c.text;
    var who = document.createElement('figcaption');
    who.className = 'sl-ugc__who';
    who.textContent = (c.author || 'Buyer') + (c.verified ? ' · Verified' : '');

    body.appendChild(stars);
    body.appendChild(quote);
    body.appendChild(who);
    fig.appendChild(media);
    fig.appendChild(body);
    return fig;
  }

  /** 5★ → 1★ bars from Judge.me's histogram; hidden when there is none. */
  function ugcBars(root) {
    var host = root.querySelector('[data-sl-ugc-bars]');
    if (!host || host.childNodes.length) return;
    var rows = document.querySelectorAll('#judgeme_product_reviews .jdgm-histogram__row[data-rating]');

    Array.prototype.forEach.call(rows, function (r) {
      var rating = r.getAttribute('data-rating');
      if (!rating || rating === 'null') return;
      var row = document.createElement('div');
      row.className = 'sl-ugc__bar';
      var label = document.createElement('span');
      label.textContent = rating + '★';
      var track = document.createElement('span');
      track.className = 'sl-ugc__track';
      var fill = document.createElement('span');
      fill.className = 'sl-ugc__fill';
      fill.style.setProperty('--w', (parseFloat(r.getAttribute('data-percentage')) || 0) + '%');
      track.appendChild(fill);
      var n = document.createElement('span');
      n.className = 'sl-ugc__n';
      n.textContent = r.getAttribute('data-frequency') || '0';
      row.appendChild(label);
      row.appendChild(track);
      row.appendChild(n);
      host.appendChild(row);
    });

    if (!host.childNodes.length) host.hidden = true;
  }

  /**
   * The design's coverflow: the active card faces the viewer, neighbours
   * swing back and fade (same maths as the design canvas).
   */
  function initCoverflow() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-ugc]'), function (root) {
      if (root.dataset.slBound) return;
      root.dataset.slBound = 'true';

      var stage = root.querySelector('[data-sl-ugc-stage]');
      var dotsHost = root.querySelector('[data-sl-ugc-dots]');
      if (!stage) return;

      if (root.getAttribute('data-source') === 'judgeme') {
        ugcFromJudgeme(root).forEach(function (c) { stage.appendChild(buildCard(c)); });
      }
      ugcBars(root);

      var cards = stage.querySelectorAll('.sl-ugc__card');
      if (!cards.length) return;
      root.hidden = false;

      var n = cards.length;
      var active = 0;
      var dots = [];
      var swiped = false;

      function layout() {
        var tallest = 0;
        Array.prototype.forEach.call(cards, function (card, i) {
          var d = i - active;
          if (d > n / 2) d -= n;
          if (d < -n / 2) d += n;
          var a = Math.abs(d);
          var sign = d < 0 ? -1 : 1;
          var x = -50 + d * 82;
          var scale = a === 0 ? 1 : Math.max(0.74, 1 - a * 0.13);
          var rot = a === 0 ? 0 : sign * Math.min(38, 26 + a * 6);
          var z = a === 0 ? 0 : -110 - (a - 1) * 90;

          card.style.transform = 'translateX(' + x + '%) translateZ(' + z + 'px) rotateY(' + -rot + 'deg) scale(' + scale.toFixed(3) + ')';
          card.style.opacity = a > 2 ? '0' : a === 0 ? '1' : '0.42';
          card.style.filter = a === 0 ? 'none' : 'blur(1.4px)';
          card.style.zIndex = String(100 - a * 10);
          card.style.boxShadow = a === 0 ? '0 26px 60px -22px rgba(22,24,28,.42)' : '0 12px 30px -18px rgba(22,24,28,.28)';
          card.style.cursor = a === 0 ? 'default' : 'pointer';
          card.setAttribute('aria-hidden', a === 0 ? 'false' : 'true');
          tallest = Math.max(tallest, card.offsetHeight);
        });
        if (tallest) stage.style.height = tallest + 28 + 'px';
        dots.forEach(function (dot, i) { dot.setAttribute('aria-current', i === active ? 'true' : 'false'); });
      }

      function go(i) {
        active = ((i % n) + n) % n;
        layout();
      }

      Array.prototype.forEach.call(cards, function (card, i) {
        card.addEventListener('click', function () {
          if (swiped) { swiped = false; return; }
          if (i !== active) go(i);
        });
        if (dotsHost && n > 1) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'sl-ugc__dot';
          dot.setAttribute('aria-label', 'Show review ' + (i + 1));
          dot.addEventListener('click', function () { go(i); });
          dotsHost.appendChild(dot);
          dots.push(dot);
        }
      });

      var prev = root.querySelector('[data-sl-ugc-prev]');
      var next = root.querySelector('[data-sl-ugc-next]');
      if (n < 2) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
      }
      if (prev) prev.addEventListener('click', function () { go(active - 1); });
      if (next) next.addEventListener('click', function () { go(active + 1); });

      stage.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(active - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); go(active + 1); }
      });

      var x0 = null;
      stage.addEventListener('pointerdown', function (e) { x0 = e.clientX; });
      stage.addEventListener('pointerup', function (e) {
        if (x0 === null) return;
        var dx = e.clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 40) {
          swiped = true;
          go(active + (dx < 0 ? 1 : -1));
        }
      });

      window.addEventListener('resize', layout);
      Array.prototype.forEach.call(stage.querySelectorAll('img'), function (im) {
        if (!im.complete) im.addEventListener('load', layout);
      });
      layout();
    });
  }

  /**
   * Collection cover flow. Unlike the review coverflow above, swiping here is
   * the browser's own scroll-snap, so momentum, keyboard and screen readers
   * need no gesture code. Each frame this reads where every card sits relative
   * to the centre, tilts it by that distance (same angles as the reviews), and
   * floods the band with the centred bottle's colour.
   */
  function initCollectionFlow() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-cf]'), function (root) {
      if (root.dataset.slBound) return;
      root.dataset.slBound = 'true';

      var track = root.querySelector('[data-sl-cf-track]');
      var items = track ? track.querySelectorAll('[data-sl-cf-item]') : [];
      if (items.length < 2) return;
      // Tilt the inner card, never the snap target, or the snap points move
      // with the tilt and the track settles off-centre. Each card carries its
      // own perspective() because the track's would not reach a grandchild.
      var cards = Array.prototype.map.call(items, function (it) {
        return it.querySelector('.sl-cf__card') || it;
      });

      // Colours are var() chains in the markup; the computed value has them
      // resolved, so read each one once up front.
      var colours = Array.prototype.map.call(items, function (it) {
        return getComputedStyle(it).getPropertyValue('--cf-c').trim();
      });
      var active = -1;
      var raf = 0;

      function frame() {
        raf = 0;
        var mid = track.scrollLeft + track.clientWidth / 2;
        var w = items[0].offsetWidth || 1;
        var best = 0;
        var bestA = Infinity;

        Array.prototype.forEach.call(items, function (it, i) {
          var d = (it.offsetLeft + it.offsetWidth / 2 - mid) / w;
          var a = Math.abs(d);
          if (a < bestA) { bestA = a; best = i; }
          if (reduced) return;
          var rot = (d < 0 ? -1 : 1) * Math.min(38, a * 34);
          var z = -Math.min(a, 2.5) * 120;
          var scale = Math.max(0.74, 1 - a * 0.13);
          cards[i].style.transform = 'perspective(1100px) translateZ(' + z.toFixed(1) + 'px) rotateY(' + (-rot).toFixed(2) + 'deg) scale(' + scale.toFixed(3) + ')';
          cards[i].style.opacity = a > 2.4 ? '0' : (1 - Math.min(a, 1) * 0.42).toFixed(3);
          it.style.zIndex = String(100 - Math.round(a * 10));
        });

        if (best !== active) {
          if (active > -1) items[active].classList.remove('is-active');
          items[best].classList.add('is-active');
          active = best;
          if (colours[best]) root.style.setProperty('--cf-bg', colours[best]);
        }
      }

      function queue() {
        if (!raf) raf = requestAnimationFrame(frame);
      }

      function go(i) {
        var it = items[Math.max(0, Math.min(items.length - 1, i))];
        track.scrollTo({
          left: it.offsetLeft + it.offsetWidth / 2 - track.clientWidth / 2,
          behavior: reduced ? 'auto' : 'smooth'
        });
      }

      // A tap on a side bottle brings it forward rather than leaving the page;
      // only the centred one opens its product.
      Array.prototype.forEach.call(items, function (it, i) {
        it.addEventListener('click', function (e) {
          if (i !== active) {
            e.preventDefault();
            go(i);
          }
        });
      });

      var prev = root.querySelector('[data-sl-cf-prev]');
      var next = root.querySelector('[data-sl-cf-next]');
      if (prev) prev.addEventListener('click', function () { go(active - 1); });
      if (next) next.addEventListener('click', function () { go(active + 1); });

      track.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue);
      root.classList.add('is-live');
      frame();
    });
  }

  /* --------------------------------------------------------- scent wheel */

  // Horizon scrolls an inner wrapper rather than the document on some widths,
  // and a scroll event on an element never reaches window. Find what scrolls.
  function scrollerOf(el) {
    for (var n = el.parentElement; n && n !== document.body; n = n.parentElement) {
      var oy = window.getComputedStyle(n).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 4) return n;
    }
    return null;
  }

  /**
   * Scent wheel. Cards ride an invisible wheel below the stage: card i sits at
   * angle (i - pos) * STEP, so the front card stands upright and neighbours
   * lean along the curve and fall away. `pos` drifts on its own, follows drags
   * (with a little inertia) and page scroll, and eases to a card when it is
   * tapped. Only the front card flips; tapping a side card brings it round.
   */
  function initScentWheel() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-wheel]'), function (root) {
      if (root.dataset.slBound) return;
      root.dataset.slBound = 'true';

      var stage = root.querySelector('[data-sl-wheel-stage]');
      var tiles = Array.prototype.slice.call(root.querySelectorAll('[data-sl-wheel-tile]'));
      if (!stage || tiles.length < 3) return;

      // Under seven cards the wheel shows gaps, so repeat the set. Copies are
      // the same products, so they are hidden from assistive tech and the tab
      // order; they stay clickable so a tap on one still works.
      var real = tiles.length;
      for (var k = 0; tiles.length < 7; k++) {
        var copy = tiles[k % real].cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        Array.prototype.forEach.call(copy.querySelectorAll('a, button'), function (el) { el.tabIndex = -1; });
        stage.appendChild(copy);
        tiles.push(copy);
      }

      var n = tiles.length;
      var STEP = 34;
      var RAD = (STEP * Math.PI) / 180;
      var drift = root.getAttribute('data-drift') !== 'false' && !reduced;
      var pos = 0;
      var vel = 0;
      var target = null;
      var R = 400;
      var flipped = null;
      var dragging = false;
      var hovering = false;
      var onScreen = false;
      var suppressClick = false;
      var x0 = 0;
      var moved = 0;
      var lastMove = 0;
      var lastT = 0;
      var raf = 0;

      function measure() {
        R = (tiles[0].offsetWidth || 260) * 1.9;
      }
      function wrap(d) {
        return ((((d + n / 2) % n) + n) % n) - n / 2;
      }
      function front() {
        return ((Math.round(pos) % n) + n) % n;
      }

      function draw() {
        var f = front();
        tiles.forEach(function (t, i) {
          var d = wrap(i - pos);
          var a = d * RAD;
          var c = Math.cos(a);
          var off = Math.abs(d) > 2.6;
          t.style.transform =
            'translate(' + (R * Math.sin(a)).toFixed(1) + 'px,' + (R * (1 - c)).toFixed(1) + 'px) ' +
            'rotate(' + (d * STEP).toFixed(2) + 'deg) scale(' + (0.88 + 0.12 * c).toFixed(3) + ')';
          t.style.zIndex = String(Math.round(100 * c));
          // Hidden, not just transparent, so cards round the back of the wheel
          // can't take focus or a stray tap.
          t.style.visibility = off ? 'hidden' : '';
          t.classList.toggle('is-front', i === f);
        });
      }

      function unflip() {
        if (!flipped) return;
        flipped.classList.remove('is-flipped');
        var b = flipped.querySelector('[data-sl-wheel-flip]');
        if (b) b.setAttribute('aria-expanded', 'false');
        flipped = null;
      }

      function loop(now) {
        raf = 0;
        var dt = lastT ? Math.min(0.05, (now - lastT) / 1000) : 0;
        lastT = now;
        if (!dragging) {
          if (target !== null) {
            pos += (target - pos) * Math.min(1, dt * 7);
            if (Math.abs(target - pos) < 0.002) { pos = target; target = null; }
          } else if (Math.abs(vel) > 0.02) {
            pos += vel * dt;
            vel *= Math.pow(0.05, dt);
          } else {
            vel = 0;
            // About one card every twelve seconds, paused while the shopper is
            // pointing at it or reading a flipped card.
            if (drift && !hovering && !flipped) pos += dt * 0.085;
          }
        }
        draw();
        if (onScreen && !document.hidden) raf = requestAnimationFrame(loop);
        else lastT = 0;
      }
      function wake() {
        if (!raf && onScreen) {
          lastT = 0;
          raf = requestAnimationFrame(loop);
        }
      }

      function goTo(i) {
        unflip();
        vel = 0;
        target = pos + wrap(i - pos);
        wake();
      }

      stage.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        dragging = true;
        moved = 0;
        vel = 0;
        target = null;
        x0 = e.clientX;
        lastMove = performance.now();
      });
      stage.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - x0;
        x0 = e.clientX;
        moved += Math.abs(dx);
        // Capture only once it is clearly a drag, so a plain tap still lands
        // on the button or link under the finger.
        if (moved > 6 && !stage.hasPointerCapture(e.pointerId)) {
          try { stage.setPointerCapture(e.pointerId); } catch (err) {}
          unflip();
        }
        var dp = -dx / (R * RAD);
        var now = performance.now();
        var dts = Math.max(0.008, (now - lastMove) / 1000);
        lastMove = now;
        vel = vel * 0.6 + (dp / dts) * 0.4;
        pos += dp;
        wake();
      });
      function endDrag() {
        if (!dragging) return;
        dragging = false;
        if (moved > 6) suppressClick = true;
        if (performance.now() - lastMove > 120) vel = 0;
        wake();
      }
      stage.addEventListener('pointerup', endDrag);
      stage.addEventListener('pointercancel', endDrag);

      // Capture phase, so a drag that ends over a link never follows it.
      stage.addEventListener('click', function (e) {
        if (suppressClick) {
          suppressClick = false;
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        var tile = e.target.closest('[data-sl-wheel-tile]');
        if (!tile) return;
        var i = tiles.indexOf(tile);
        if (i !== front()) {
          e.preventDefault();
          goTo(i);
          return;
        }
        if (e.target.closest('a')) return;
        if (tile.classList.contains('is-flipped')) {
          unflip();
        } else {
          unflip();
          tile.classList.add('is-flipped');
          var b = tile.querySelector('[data-sl-wheel-flip]');
          if (b) b.setAttribute('aria-expanded', 'true');
          flipped = tile;
          // The drift leaves the front card part-way round the curve. Bring it
          // square to the viewer while it turns over, so the back reads level.
          vel = 0;
          target = pos + wrap(i - pos);
        }
        wake();
      }, true);

      root.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        var next = (((front() + (e.key === 'ArrowRight' ? 1 : -1)) % n) + n) % n;
        goTo(next);
        // Follow with focus, except onto a repeated copy, which is hidden from
        // assistive tech; focus then simply stays where it was.
        var b = tiles[next].querySelector('[data-sl-wheel-flip]');
        if (b && !tiles[next].hasAttribute('aria-hidden')) b.focus({ preventScroll: true });
      });

      if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        stage.addEventListener('pointerenter', function () { hovering = true; });
        stage.addEventListener('pointerleave', function () { hovering = false; wake(); });
      }

      // Scrolling the page past the wheel turns it a little, as on Dossier.
      var scroller = scrollerOf(root);
      var lastY = null;
      function onScroll() {
        var y = scroller ? scroller.scrollTop : window.pageYOffset;
        if (onScreen && !reduced && !dragging && !flipped && lastY !== null) {
          pos += (y - lastY) / 900;
          wake();
        }
        lastY = y;
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      if (scroller) scroller.addEventListener('scroll', onScroll, { passive: true });

      if (typeof IntersectionObserver !== 'undefined') {
        new IntersectionObserver(function (entries) {
          onScreen = entries[0].isIntersecting;
          if (onScreen) wake();
        }, { rootMargin: '80px 0px' }).observe(root);
      } else {
        onScreen = true;
      }
      document.addEventListener('visibilitychange', function () { if (!document.hidden) wake(); });
      window.addEventListener('resize', function () { measure(); draw(); });

      measure();
      root.classList.add('is-live');
      draw();
      wake();
    });
  }

  /* ---------------------------------------------------------- note tiers */

  /**
   * Top / Heart / Base columns (.sl-tri). Tapping a column that has more notes
   * opens its panel under the columns and closes any other; tapping it again
   * closes it. With a mouse, resting on a column opens it as well, and it
   * stays open after the pointer leaves so the notes can be read; a click
   * straight after that keeps it open rather than closing it again.
   */
  function initTriNotes() {
    var hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');

    Array.prototype.forEach.call(document.querySelectorAll('[data-sl-tri]'), function (root) {
      if (root.dataset.slTriReady) return;
      root.dataset.slTriReady = 'true';

      var cols = root.querySelectorAll('[data-sl-tri-col]');
      if (!cols.length) return;
      var timer = null;
      var hovOpened = null;

      // Lazy images in a closed (display: none) panel would only start
      // loading once it opens, popping in blank; fetch them as the tiers
      // come near the screen instead.
      function warm() {
        Array.prototype.forEach.call(root.querySelectorAll('.sl-tri__panel img[loading="lazy"]'), function (img) {
          img.loading = 'eager';
        });
      }
      if (typeof IntersectionObserver === 'undefined') {
        warm();
      } else {
        var io = new IntersectionObserver(function (entries) {
          if (!entries.some(function (e) { return e.isIntersecting; })) return;
          io.disconnect();
          warm();
        }, { rootMargin: '300px 0px' });
        io.observe(root);
      }

      function set(col) {
        Array.prototype.forEach.call(cols, function (c) {
          var on = c === col;
          c.setAttribute('aria-expanded', on ? 'true' : 'false');
          var panel = document.getElementById(c.getAttribute('aria-controls'));
          if (panel) panel.classList.toggle('is-open', on);
        });
        root.classList.toggle('has-open', !!col);
      }

      Array.prototype.forEach.call(cols, function (c) {
        c.addEventListener('click', function () {
          if (hovOpened === c) { hovOpened = null; return; }
          hovOpened = null;
          set(c.getAttribute('aria-expanded') === 'true' ? null : c);
        });
        c.addEventListener('mouseenter', function () {
          if (!hoverable.matches) return;
          clearTimeout(timer);
          timer = setTimeout(function () {
            if (c.getAttribute('aria-expanded') === 'true') return;
            set(c);
            hovOpened = c;
          }, 140);
        });
        c.addEventListener('mouseleave', function () {
          clearTimeout(timer);
          if (hovOpened === c) hovOpened = null;
        });
      });

      root.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || !root.classList.contains('has-open')) return;
        var open = root.querySelector('[data-sl-tri-col][aria-expanded="true"]');
        set(null);
        if (open) open.focus();
      });

      // Peek, once per visit: when the tiers are well in view and the photos
      // have popped in, two of the first tier's hidden notes pop out of its
      // lead photo and tuck back, then its "+N more" pill nudges, so shoppers
      // see there is more behind each photo. Skipped if they have already
      // opened a tier, or prefer reduced motion.
      if (reduced || typeof IntersectionObserver === 'undefined') return;
      var peeked = false;
      var seen = new IntersectionObserver(function (entries) {
        if (peeked || !entries.some(function (e) { return e.isIntersecting; })) return;
        peeked = true;
        seen.disconnect();
        setTimeout(function () { peek(cols[0]); }, 1500);
      }, { threshold: 0.6 });
      seen.observe(root);

      function peek(col) {
        if (!col || root.classList.contains('has-open')) return;
        var note = col.querySelector('.sl-pyr__note');
        var panel = document.getElementById(col.getAttribute('aria-controls'));
        if (!note || !panel) return;
        var imgs = panel.querySelectorAll('.sl-pyr__ico img');
        ['l', 'r'].forEach(function (side, k) {
          var src = imgs[k] && (imgs[k].currentSrc || imgs[k].src);
          if (!src) return;
          var bubble = document.createElement('span');
          bubble.className = 'sl-tri__peek sl-tri__peek--' + side;
          bubble.setAttribute('aria-hidden', 'true');
          var im = document.createElement('img');
          im.src = src;
          im.alt = '';
          bubble.appendChild(im);
          note.appendChild(bubble);
          bubble.addEventListener('animationend', function () { bubble.remove(); });
        });
        var pill = col.querySelector('.sl-tri__more');
        if (pill) {
          setTimeout(function () {
            pill.classList.add('is-nudge');
            pill.addEventListener('animationend', function done() {
              pill.classList.remove('is-nudge');
              pill.removeEventListener('animationend', done);
            });
          }, 4000);
        }
      }
    });
  }

  /* ------------------------------------------------------------------ boot */

  /* ------------------------------------------------------- sticky buy bar */

  /**
   * Shows the sticky bar once the product's own Add to bag button has been
   * scrolled past (and the footer isn't in view). Not before: on a phone the
   * button starts below the first screen, and a bar there would cover the
   * rating, price and video stories. Its button clicks the real one, so
   * Horizon's add-to-cart flow and the cart drawer run unchanged.
   */
  function initStickyBuy() {
    var bars = document.querySelectorAll('[data-sl-sticky]');

    Array.prototype.forEach.call(bars, function (bar) {
      if (bar.dataset.slBound) return;
      bar.dataset.slBound = 'true';

      var section = bar.closest('.shopify-section') || document;
      var buy = section.querySelector('.sl-buy');
      var realBtn = section.querySelector('[data-sl-atc]');
      var btn = bar.querySelector('[data-sl-sticky-btn]');

      // A theme-editor re-render leaves the previous bar in <body>.
      Array.prototype.forEach.call(document.querySelectorAll('body > [data-sl-sticky]'), function (old) {
        if (old !== bar && old.dataset.section === bar.dataset.section) old.remove();
      });
      document.body.appendChild(bar);

      if (btn && realBtn) {
        btn.addEventListener('click', function () {
          if (!realBtn.disabled) realBtn.click();
        });
      }
      if (!buy || typeof IntersectionObserver === 'undefined') return;

      var buyPassed = false;
      var footerInView = false;
      function update() {
        var show = buyPassed && !footerInView;
        bar.classList.toggle('is-shown', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (btn) btn.tabIndex = show ? 0 : -1;
      }

      // Measured on scroll rather than with an IntersectionObserver: a jump
      // past the button (the "Read all reviews" link) never intersects it, so
      // an observer would not fire. Capture catches whichever element scrolls
      // (Horizon scrolls .page-wrapper at some widths).
      var ticking = false;
      function measure() {
        ticking = false;
        var passed = buy.getBoundingClientRect().bottom < 0;
        if (passed !== buyPassed) { buyPassed = passed; update(); }
      }
      document.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(measure);
      }, { passive: true, capture: true });
      measure();

      var footer = document.querySelector('.shopify-section-group-footer-group, footer');
      if (footer) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { footerInView = e.isIntersecting; });
          update();
        }).observe(footer);
      }
    });
  }

  /* ------------------------------------------------------ recommendations */

  /**
   * "Layer it with": fetches the section through Shopify's recommendations
   * endpoint, drops the product being viewed, keeps the configured number of
   * cards, then animates them in when they scroll into view.
   */
  function initRecs() {
    var roots = document.querySelectorAll('[data-sl-recs]');

    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.slLoaded || !root.dataset.url || !root.dataset.productId) return;
      root.dataset.slLoaded = 'true';

      fetch(root.dataset.url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-sl-recs]');
          if (!fresh || !fresh.innerHTML.trim()) return;

          root.innerHTML = fresh.innerHTML;

          var limit = parseInt(root.dataset.limit, 10) || 4;
          var shown = 0;
          Array.prototype.forEach.call(root.querySelectorAll('.sl-recs__item'), function (item) {
            if (item.dataset.recId === root.dataset.productId || shown >= limit) {
              item.remove();
              return;
            }
            item.style.setProperty('--i', shown);
            shown++;
          });
          if (!shown) return;
          root.hidden = false;

          var targets = root.querySelectorAll('.sl-anim, .sl-rev');
          if (reduced || typeof IntersectionObserver === 'undefined') {
            revealAll(targets);
            return;
          }
          var io = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (e) {
                if (!e.isIntersecting) return;
                arrive(e.target);
                io.unobserve(e.target);
              });
            },
            { threshold: 0.15 }
          );
          Array.prototype.forEach.call(targets, function (t) { io.observe(t); });
        })
        .catch(function () {});
    });
  }

  /* ------------------------------------------------------- size selection */

  /**
   * The size buttons are not the form's variant input — Horizon's product form
   * needs a single [ref="variantId"] field to take over the submit. Choosing a
   * size writes into that field and updates the price and the button.
   */
  function initSizePicker() {
    var inputs = document.querySelectorAll('[data-sl-variant-input]');

    Array.prototype.forEach.call(inputs, function (hidden) {
      var form = hidden.closest('form');
      if (!form || form.dataset.slSize) return;
      form.dataset.slSize = 'true';

      form.addEventListener('change', function (event) {
        var radio = event.target.closest ? event.target.closest('[data-sl-size]') : null;
        if (!radio) return;

        hidden.value = radio.value;

        var money = radio.dataset.price || '';
        var price = document.getElementById('ShorelinePrice');
        if (price && money) price.textContent = money;

        var button = form.querySelector('[data-sl-atc]');
        if (button && button.dataset.label) {
          button.textContent =
            button.dataset.showPrice === 'true' && money
              ? button.dataset.label + ' — ' + money
              : button.dataset.label;
        }

        // The sticky bar lives in <body>, so it is updated by attribute.
        var variantRow = radio.closest('.sl-variant');
        var sizeText = variantRow && variantRow.querySelector('.sl-variant__ml');
        Array.prototype.forEach.call(document.querySelectorAll('[data-sl-sticky-price]'), function (el) {
          if (money) el.textContent = money;
        });
        Array.prototype.forEach.call(document.querySelectorAll('[data-sl-sticky-size]'), function (el) {
          if (sizeText) el.textContent = sizeText.textContent;
        });
      });
    });
  }

  /* ------------------------------------------------------- note-hour bars */

  /**
   * The hour-by-hour bars. Every column already carries its own numbers, so
   * this only moves them into the readout — the chart and the readout are both
   * correct with the script switched off.
   */
  function initBars() {
    var charts = document.querySelectorAll('[data-sl-bars]');

    Array.prototype.forEach.call(charts, function (chart) {
      if (chart.dataset.slBound) return;
      chart.dataset.slBound = 'true';

      var cols = chart.querySelectorAll('.sl-bars__col');
      if (!cols.length) return;

      var when = chart.querySelector('[data-sl-read-when]');
      var strength = chart.querySelector('[data-sl-read-strength]');
      var lead = chart.querySelector('[data-sl-read-lead]');
      var meters = chart.querySelectorAll('[data-sl-mix]');
      var pcts = chart.querySelectorAll('[data-sl-pct]');
      var home = cols[parseInt(chart.dataset.default, 10) || 0] || cols[0];
      var pinned = null;

      function read(col) {
        if (!col) return;
        if (when) when.textContent = col.dataset.when || '';
        if (strength) strength.textContent = (col.dataset.strength || '0') + '% strength';
        if (lead) {
          lead.textContent = col.dataset.lead || '';
          lead.style.setProperty('--c', col.dataset.leadC || '');
        }
        Array.prototype.forEach.call(meters, function (m) {
          m.style.width = (col.dataset['p' + m.dataset.slMix] || 0) + '%';
        });
        Array.prototype.forEach.call(pcts, function (p) {
          p.textContent = (col.dataset['p' + p.dataset.slPct] || 0) + '%';
        });
        Array.prototype.forEach.call(cols, function (c) { c.classList.toggle('is-on', c === col); });
      }

      Array.prototype.forEach.call(cols, function (col) {
        // Hover and keyboard focus preview an hour; a click or tap keeps it,
        // which is the only way to hold one on a touch screen.
        col.addEventListener('pointerenter', function () { if (!pinned) read(col); });
        col.addEventListener('focus', function () { read(col); });
        // A click always keeps that hour — never a toggle, so a double tap or
        // a stray second click cannot bounce the reading back to the start.
        col.addEventListener('click', function () { pinned = col; read(col); });
      });

      chart.addEventListener('pointerleave', function () { read(pinned || home); });
    });
  }

  /* ------------------------------------------------------------ scroll hero */

  /**
   * Publishes four numbers and lets CSS do everything with them: --p for the
   * whole pinned scroll, and --a1/--a2/--a3 for the three acts, each easing
   * from 0 to 1 across its own slice of it. One rAF-throttled scroll listener
   * that sleeps while the hero is off screen; no layout is read per layer.
   */
  function initHero3() {
    var heroes = document.querySelectorAll('[data-sl-hero3]');

    // Act boundaries as fractions of the pinned scroll.
    var ACTS = [[0, 0.3], [0.26, 0.66], [0.62, 1]];

    function slice(p, from, to) {
      return Math.min(1, Math.max(0, (p - from) / (to - from)));
    }
    /** Ease-in-out, so an act settles rather than stopping dead. */
    function ease(t) {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    Array.prototype.forEach.call(heroes, function (hero) {
      if (hero.dataset.slHero3) return;
      hero.dataset.slHero3 = 'true';
      if (reduced) return;

      var stage = hero.querySelector('[data-sl-hero3-stage]');
      var film = hero.querySelector('[data-sl-hero3-film]');
      var onScreen = true;
      var ticking = false;

      /**
       * The film is a luxury, so it has to earn its download: skip it for
       * anyone who asked for less motion, anyone on Data Saver, and anyone on a
       * slow connection. They keep the still plate, which has already loaded.
       */
      function filmIsWelcome() {
        if (!film || !film.dataset.src) return false;
        var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (c && c.saveData) return false;
        if (c && /(^|-)(2g|slow-2g)$/.test(c.effectiveType || '')) return false;
        return true;
      }

      function startFilm() {
        if (!filmIsWelcome()) return;
        if (!film.dataset.slLoaded) {
          film.dataset.slLoaded = 'true';
          film.src = film.dataset.src;
          // Only show it once frames are actually running, so a stalled load
          // never blanks the plate. is-film also stands the ingredient ring
          // down — the film is already carrying the ingredients.
          film.addEventListener('playing', function () {
            film.classList.add('is-live');
            hero.classList.add('is-film');
          }, { once: true });
        }
        var playing = film.play();
        if (playing && playing.catch) playing.catch(function () {});
      }

      function frame() {
        ticking = false;
        var r = hero.getBoundingClientRect();
        // The pin is one viewport tall, so the travel is what is left of the
        // section once that is taken off.
        var travel = Math.max(1, r.height - window.innerHeight);
        var p = Math.min(1, Math.max(0, -r.top / travel));
        hero.style.setProperty('--p', p.toFixed(4));
        for (var i = 0; i < ACTS.length; i++) {
          hero.style.setProperty('--a' + (i + 1), ease(slice(p, ACTS[i][0], ACTS[i][1])).toFixed(4));
        }
      }

      function onScroll() {
        if (ticking || !onScreen) return;
        ticking = true;
        requestAnimationFrame(frame);
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });

      if (typeof IntersectionObserver !== 'undefined') {
        new IntersectionObserver(function (entries) {
          onScreen = entries[0].isIntersecting;
          if (onScreen) {
            onScroll();
            startFilm();
          } else if (film && film.dataset.slLoaded) {
            // Nothing decodes while it is off screen.
            film.pause();
          }
        }).observe(hero);
      } else if (film) {
        startFilm();
      }

      // A backgrounded tab should not be decoding video either.
      document.addEventListener('visibilitychange', function () {
        if (!film || !film.dataset.slLoaded) return;
        if (document.hidden) film.pause();
        else if (onScreen) startFilm();
      });

      // Parallax tilt, mouse only — a touch screen has nothing to point with.
      if (stage && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        hero.addEventListener('pointermove', function (e) {
          var b = hero.getBoundingClientRect();
          var x = (e.clientX - b.left) / b.width - 0.5;
          var y = (e.clientY - b.top) / b.height - 0.5;
          stage.style.setProperty('--ry', (x * 9).toFixed(2) + 'deg');
          stage.style.setProperty('--rx', (-y * 6).toFixed(2) + 'deg');
        });
        hero.addEventListener('pointerleave', function () {
          stage.style.removeProperty('--rx');
          stage.style.removeProperty('--ry');
        });
      }

      frame();
    });
  }

  function init() {
    initReveal();
    initHero3();
    initBars();
    initSizePicker();
    initStickyBuy();
    initRecs();
    initFamilyTilt();
    initCarousels();
    initFilters();
    initQty();
    initGallery();
    initStories();
    initQuotes();
    initVideoReviews();
    initTriNotes();
    if (typeof initCoverflow === 'function') initCoverflow();
    initCollectionFlow();
    initScentWheel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor swaps section markup without a page load.
  document.addEventListener('shopify:section:load', init);
})();


// Luxe menu: "WhatsApp us" closes the drawer and opens the chat widget
// when it is installed; otherwise the link falls through to the contact page.
(function () {
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-sl-menu-wa]');
    if (!link) return;
    var btn = document.querySelector('#rawa-container #rawa-button');
    if (!btn) return;
    e.preventDefault();
    var drawer = link.closest('header-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
    setTimeout(function () { btn.click(); }, 350);
  });
})();